import { type JSX, useState } from "react";

import NotEnoughSeats from "features/subscriptions/components/NotEnoughSeatsDialog";

import { LinkedInLogin } from "common/components/LinkedInLogin";
import { Dialog, DialogContent } from "common/components/ui/Dialog";
import { Drawer, DrawerContent } from "common/components/ui/Drawer";
import useAvailableLicensesCount from "common/hooks/useAvailableLicensesCount";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

import AssignLicense from "../components/AccountActions/AssignLicense";
import DeleteAccount from "../components/AccountActions/DeleteAccount";
import RemoveCancellation from "../components/AccountActions/RemoveCancellation";
import RemoveLicense from "../components/AccountActions/RemoveLicense";
import BrandedLoginUrl from "../components/BrandedLoginUrl";

type AccountAction =
  | "addLicense"
  | "assignLicense"
  | "removeLicense"
  | "delete"
  | "reconnect"
  | "reconnectLink" // Generate reconnect link for branded login dashboard
  | "reactivate"
  | "settings";

export default function useAccountActions(account: WorkspaceAccount) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);
  const [action, setAction] = useState<AccountAction>();
  const { availableLicensesCount } = useAvailableLicensesCount();

  function selectAction(action: Exclude<AccountAction, "addLicense">) {
    if (action === "assignLicense" && !availableLicensesCount) {
      setAction("addLicense");
    } else {
      setAction(action);
    }
    setIsOpen(true);
  }
  function closeDialog() {
    setIsOpen(false);
  }

  let actionComponent: JSX.Element;

  if (action === "assignLicense") {
    actionComponent = (
      <AssignLicense
        accountFullname={account.full_name}
        accountId={account.id}
        goBack={closeDialog}
        onSuccess={closeDialog}
      />
    );
  } else if (action === "removeLicense") {
    actionComponent = (
      <RemoveLicense
        accountFullname={account.full_name}
        accountId={account.id}
        goBack={closeDialog}
        onSuccess={closeDialog}
      />
    );
  } else if (action === "delete") {
    actionComponent = (
      <DeleteAccount
        accountFullname={account.full_name}
        accountId={account.id}
        goBack={closeDialog}
        onSuccess={closeDialog}
      />
    );
  } else if (action === "reconnect") {
    actionComponent = (
      <LinkedInLogin account={account} onSuccess={closeDialog} />
    );
  } else if (action === "reactivate") {
    actionComponent = (
      <RemoveCancellation
        accountId={account.id}
        onSuccess={closeDialog}
        goBack={closeDialog}
      />
    );
  } else if (action === "reconnectLink") {
    actionComponent = (
      <BrandedLoginUrl accountId={account.id} goBack={closeDialog} />
    );
  }

  const Container = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;

  const components =
    action === "addLicense" ? (
      <NotEnoughSeats
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        noSeatAccountIds={[account.id]}
      />
    ) : (
      <Container open={isOpen} onOpenChange={setIsOpen}>
        <Content size={action === "reconnect" ? "lg" : "md"}>
          {actionComponent}
        </Content>
      </Container>
    );
  return { selectAction, components };
}
